import { Card, Button, Badge, FormSelect, FormNumberInput } from '@/components/UI';
import { FaSync } from 'react-icons/fa';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import type { AvailableAnalysis, ParameterAnalysisSummaryOutput, ParameterDiscoveryJob, ParameterDiscoveryMetric } from './types';

interface AnalysisScopeCardProps {
    analysisResult: ParameterAnalysisSummaryOutput | undefined;
    availableDurations: string[];
    availableHorizons: number[];
    cachedAnalyses: AvailableAnalysis[] | undefined;
    isQueueJobPending: boolean;
    jobs: ParameterDiscoveryJob[] | undefined;
    minEntriesThreshold: number;
    onRefresh: () => void;
    optimizationMode: ParameterDiscoveryMetric;
    selectedDuration: string;
    selectedHorizon: number;
    setMinEntriesThreshold: (value: number) => void;
    setOptimizationMode: (mode: ParameterDiscoveryMetric) => void;
    setSelectedDuration: (duration: string) => void;
    setSelectedHorizon: (horizonSeconds: number) => void;
}

const AnalysisScopeCard = ({
    analysisResult,
    availableDurations,
    availableHorizons,
    cachedAnalyses,
    isQueueJobPending,
    jobs,
    minEntriesThreshold,
    onRefresh,
    optimizationMode,
    selectedDuration,
    selectedHorizon,
    setMinEntriesThreshold,
    setOptimizationMode,
    setSelectedDuration,
    setSelectedHorizon
}: AnalysisScopeCardProps) => {
    return (
        <Card className="bg-zinc-900 p-4">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <FormSelect
                        noMargin
                        className="min-w-[120px]"
                        label="Duration"
                        options={availableDurations.map((duration) => ({
                            value: duration,
                            label: duration
                        }))}
                        value={selectedDuration}
                        onChange={(e) => setSelectedDuration(e.target.value)}
                    />
                    <FormSelect
                        noMargin
                        className="min-w-[140px]"
                        label="Time Horizon"
                        options={availableHorizons.map((horizon) => ({
                            value: horizon.toString(),
                            label: formatTimeHorizon(horizon)
                        }))}
                        value={selectedHorizon.toString()}
                        onChange={(e) => setSelectedHorizon(parseInt(e.target.value))}
                    />
                    <div className="flex flex-col">
                        <span className="mb-1 text-xs text-zinc-400">Optimization Mode</span>
                        <div aria-label="Optimization Mode" className="flex items-center gap-2 rounded-lg bg-zinc-800 p-1" role="group">
                            <button
                                aria-pressed={optimizationMode === 'stock'}
                                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                                    optimizationMode === 'stock' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-zinc-200'
                                }`}
                                onClick={() => setOptimizationMode('stock')}
                            >
                                Stock
                            </button>
                            <button
                                aria-pressed={optimizationMode === 'options'}
                                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                                    optimizationMode === 'options' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-zinc-200'
                                }`}
                                onClick={() => setOptimizationMode('options')}
                            >
                                Options
                            </button>
                        </div>
                    </div>
                    <FormNumberInput
                        integer
                        className="min-w-[120px]"
                        fullWidth={false}
                        label="Min Entries"
                        min={0}
                        title="UI filter: show only parameter values with this many entries."
                        value={minEntriesThreshold}
                        onValueChange={(value) => {
                            // The threshold always has a value, so an empty field keeps the current one.
                            if (value === null) return;

                            setMinEntriesThreshold(value);
                        }}
                    />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {(() => {
                        const cachedList = cachedAnalyses || [];

                        const hasExactCached = cachedList.some(
                            (a: { duration: string; horizonSeconds: number }) =>
                                a.duration === selectedDuration && a.horizonSeconds === selectedHorizon
                        );

                        const job = jobs?.find(
                            (j: { duration: string; horizonSeconds: number }) =>
                                j.duration === selectedDuration && j.horizonSeconds === selectedHorizon
                        );

                        if (job && (job.status === 'running' || job.status === 'pending')) {
                            const label = job.status === 'running' ? 'Running' : 'Queued';
                            const variant = job.status === 'running' ? 'warning' : 'secondary';
                            return <Badge variant={variant}>{label}</Badge>;
                        }

                        if (job?.status === 'failed') {
                            return <Badge variant="danger">Failed</Badge>;
                        }

                        if (hasExactCached) {
                            return <Badge variant="success">Cached</Badge>;
                        }

                        if (job?.status === 'completed') {
                            return <Badge variant="success">Completed</Badge>;
                        }

                        return null;
                    })()}
                    {(() => {
                        const settingsChanged =
                            !!analysisResult &&
                            (analysisResult.duration !== selectedDuration || analysisResult.horizonSeconds !== selectedHorizon);

                        const hasExactCached = (cachedAnalyses || []).some(
                            (a: { duration: string; horizonSeconds: number }) =>
                                a.duration === selectedDuration && a.horizonSeconds === selectedHorizon
                        );

                        const pendingResult = analysisResult && 'pipelineStatus' in analysisResult ? analysisResult : null;
                        const needsRepair = !!pendingResult?.pipelineStatus?.nextRepairAction;

                        const buttonLabel = isQueueJobPending
                            ? 'Starting...'
                            : settingsChanged || needsRepair || !hasExactCached
                              ? 'Start/Repair Analysis'
                              : 'Refresh Analysis';

                        return (
                            <>
                                {settingsChanged && <span className="text-sm text-amber-400">Settings changed</span>}
                                <Button
                                    className="inline-flex items-center"
                                    disabled={isQueueJobPending}
                                    size="sm"
                                    variant="secondary"
                                    onClick={onRefresh}
                                >
                                    <FaSync className={isQueueJobPending ? 'mr-2 animate-spin' : 'mr-2'} />
                                    {buttonLabel}
                                </Button>
                            </>
                        );
                    })()}
                </div>
            </div>
        </Card>
    );
};

export default AnalysisScopeCard;
